import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";

// Shared chrome for the signed-out admin screens, so sign-in, forgot and reset all look alike.
export const AdminAuthShell = ({ heading, children, footer }) => (
  <div className="min-h-screen bg-charcoal flex items-center justify-center px-5">
    <div className="w-full max-w-md">
      <div className="text-center mb-10">
        <div className="display text-[26px] text-white">Human Home Living</div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-sage mt-1">Admin &amp; CMS</div>
      </div>
      <div className="bg-white px-8 py-9">
        <h1 className="display text-[21px] text-charcoal mb-2">{heading}</h1>
        {children}
      </div>
      <p className="text-center text-[12px] font-light text-[#A6A39D] mt-6">{footer}</p>
    </div>
  </div>
);

export const AdminAuthField = ({ label, ...props }) => (
  <label className="block mb-5">
    <span className="text-[12px] tracking-[0.14em] uppercase text-[#6E6A63]">{label}</span>
    <input
      {...props}
      className="w-full border border-line px-4 py-3.5 mt-2 text-[15.5px] focus:outline-none focus:border-sage transition-colors"
    />
  </label>
);

export const AdminAuthButton = ({ busy, children, ...props }) => (
  <button
    {...props}
    type="submit"
    disabled={busy}
    className="w-full py-4 bg-charcoal text-white text-[12.5px] tracking-[0.16em] uppercase hover:bg-sage transition-colors duration-300 disabled:opacity-60"
  >
    {children}
  </button>
);

export default function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/password-reset/request", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminAuthShell
      heading="Reset your password"
      footer="Access is restricted to Human Home Living staff."
    >
      {sent ? (
        <div data-testid="admin-forgot-sent">
          <p className="text-[14.5px] text-[#454545] leading-relaxed">
            If that address belongs to an administrator account, a reset link is on its way. It
            works once and expires in 30 minutes.
          </p>
          <Link
            to="/admin/login"
            data-testid="admin-forgot-back"
            className="inline-block mt-6 text-[13px] text-sage hover:text-sage-dark"
          >
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="admin-forgot-form">
          <p className="text-[14px] text-[#6E6A63] leading-relaxed mb-5">
            Enter the administrator email address and we will send a link to choose a new password.
          </p>
          <AdminAuthField
            label="Email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-forgot-email"
          />
          {error && (
            <p data-testid="admin-forgot-error" className="text-[13.5px] text-[#C0392B] mb-5">
              {error}
            </p>
          )}
          <AdminAuthButton busy={busy} data-testid="admin-forgot-submit">
            {busy ? "Sending…" : "Send reset link"}
          </AdminAuthButton>
          <Link
            to="/admin/login"
            className="block text-center mt-5 text-[13px] text-[#6E6A63] hover:text-charcoal"
          >
            Back to sign in
          </Link>
        </form>
      )}
    </AdminAuthShell>
  );
}
